import { useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import type { ObjectView, TableModel, ZoneView } from '../model/table';
import type { Board, BoardEdge, BoardNode } from '../protocol/ui';
import { usePresentation } from '../presentation/PresentationContext';
import { seatColorVar } from './seatColors';

interface Props {
  zone: ZoneView;
  table: TableModel;
  selectable: Set<string>;
  inspected: string | null;
  onInspect: (id: string) => void;
  seatIndex: Map<string, number>;
  zoom?: boolean;
}

interface View { x: number; y: number; w: number; h: number }

/**
 * Generic SVG board: regions, edges divided into segments (parallel edges side by side), nodes,
 * and objects placed by `at` or `ref`. Geometry is presentation only; nothing here decides legality.
 */
export function BoardView({ zone, table, selectable, inspected, onInspect, seatIndex, zoom = true }: Props) {
  const api = usePresentation();
  const board = table.boards.find(b => b.id === zone.boardId);
  const layout = useMemo(() => (board ? boardLayout(board) : null), [board]);
  const full: View = board ? { x: 0, y: 0, w: board.width, h: board.height } : { x: 0, y: 0, w: 1, h: 1 };
  const [view, setView] = useState<View>(full);
  const drag = useRef<{ x: number; y: number; view: View; moved: boolean } | null>(null);
  const svg = useRef<SVGSVGElement | null>(null);
  if (!board || !layout) return <p className="muted">This board has no geometry.</p>;

  const byRef = new Map<string, ObjectView>();
  const byNode = new Map<string, ObjectView[]>();
  for (const o of zone.objects) {
    if (o.ref) byRef.set(o.ref, o);
    if (o.at) byNode.set(o.at, [...(byNode.get(o.at) ?? []), o]);
  }
  const unit = layout.unit;
  const ownerColor = (o: ObjectView | undefined) => (o?.owner ? seatColorVar(seatIndex.get(o.owner) ?? 0) : undefined);
  const fillOf = (o: ObjectView | undefined) => {
    if (!o) return undefined;
    const fill = api.render(o, 'board')?.slots.fill;
    return fill?.kind === 'color' ? fill.value : undefined;
  };
  const activate = (o: ObjectView) => {
    if (drag.current?.moved) return;
    const target = api.pickTargets?.values.get(o.id);
    if (target !== undefined) api.pickTargets!.answer(target); else onInspect(o.id);
  };
  const interactive = (o: ObjectView | undefined) => o ? {
    role: 'button', tabIndex: 0,
    'aria-label': o.label + (api.pickTargets?.values.has(o.id) ? '. Choose this.' : selectable.has(o.id) ? '. Has available moves.' : ''),
    onClick: () => activate(o),
    onKeyDown: (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(o); } },
  } : {};
  const stateClass = (o: ObjectView | undefined) => o
    ? `${selectable.has(o.id) || api.pickTargets?.values.has(o.id) ? ' is-selectable' : ''}${inspected === o.id ? ' is-selected' : ''}` : '';

  const zoomBy = (factor: number) => setView(v => {
    const w = Math.min(full.w, Math.max(full.w / 8, v.w * factor)), h = w * (full.h / full.w);
    const cx = v.x + v.w / 2, cy = v.y + v.h / 2;
    return clampView({ x: cx - w / 2, y: cy - h / 2, w, h }, full);
  });
  const onPointerDown = (e: PointerEvent<SVGSVGElement>) => {
    if (!zoom || view.w >= full.w) return;
    drag.current = { x: e.clientX, y: e.clientY, view, moved: false };
  };
  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    if (!d || !svg.current) return;
    const rect = svg.current.getBoundingClientRect();
    const dx = (e.clientX - d.x) * (d.view.w / rect.width), dy = (e.clientY - d.y) * (d.view.h / rect.height);
    if (Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) > 4) { d.moved = true; svg.current.setPointerCapture(e.pointerId); }
    if (d.moved) setView(clampView({ ...d.view, x: d.view.x - dx, y: d.view.y - dy }, full));
  };
  const onPointerUp = () => { setTimeout(() => { drag.current = null; }, 0); };

  return (
    <div className="board-frame">
      {zoom && (
        <div className="board-tools" role="toolbar" aria-label={`${board.label} zoom`}>
          <button type="button" className="icon-button" aria-label="Zoom in" onClick={() => zoomBy(0.7)}>+</button>
          <button type="button" className="icon-button" aria-label="Zoom out" onClick={() => zoomBy(1 / 0.7)} disabled={view.w >= full.w}>−</button>
          <button type="button" className="icon-button" aria-label="Show the whole board" onClick={() => setView(full)} disabled={view.w >= full.w}>⤢</button>
        </div>
      )}
      <svg ref={svg} className={`board${view.w < full.w ? ' is-zoomed' : ''}`} viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`} role="group" aria-label={board.label}
        style={{ '--board-unit': `${unit}px` } as React.CSSProperties}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
        onDoubleClick={zoom ? () => zoomBy(0.6) : undefined}>
        <g className="board-regions">
          {board.regions.map(r => {
            const o = byRef.get(r.id);
            return (
              <polygon key={r.id} points={r.points.map(p => p.join(',')).join(' ')} className={`board-region${stateClass(o)}`}
                style={o ? { fill: fillOf(o) ?? ownerColor(o) } : undefined} {...interactive(o)}><title>{o?.label ?? r.label}</title></polygon>
            );
          })}
        </g>
        <g className="board-edges">
          {board.edges.map(e => {
            const a = layout.nodes.get(e.a), b = layout.nodes.get(e.b);
            if (!a || !b) return null;
            const o = byRef.get(e.id);
            const style = { ...(o?.owner ? { '--owner-color': ownerColor(o) } : {}), ...(fillOf(o) ? { '--edge-color': fillOf(o) } : {}) } as React.CSSProperties;
            return (
              <g key={e.id} className={`board-edge${o?.owner ? ' is-owned' : ''}${stateClass(o)}`} style={style} {...interactive(o)}>
                <title>{o?.label ?? e.label}</title>
                {edgeSegments(e, a, b, unit, layout.offsets.get(e.id) ?? 0)}
              </g>
            );
          })}
        </g>
        <g className="board-nodes">
          {board.nodes.map(n => {
            const o = byRef.get(n.id);
            const tokens = byNode.get(n.id) ?? [];
            return (
              <g key={n.id} className={`board-node${stateClass(o)}`} {...interactive(o)}>
                <title>{o?.label ?? n.label}</title>
                <circle cx={n.x} cy={n.y} r={unit * 1.3} style={o?.owner ? { fill: ownerColor(o) } : undefined} />
                <text x={n.x} y={n.y - unit * 2} className="board-label" fontSize={unit * 1.9}>{n.label}</text>
                {tokens.map((t, i) => (
                  <circle key={t.id} cx={n.x + (i - (tokens.length - 1) / 2) * unit * 2.2} cy={n.y + unit * 2.8} r={unit}
                    className={`board-token${stateClass(t)}`} style={{ fill: fillOf(t) ?? ownerColor(t) }} {...interactive(t)}><title>{t.label}</title></circle>
                ))}
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}

function clampView(v: View, full: View): View {
  return { ...v, x: Math.min(Math.max(v.x, 0), full.w - v.w), y: Math.min(Math.max(v.y, 0), full.h - v.h) };
}

/** Size unit from the board's own density, and perpendicular offsets for parallel edges. */
export function boardLayout(board: Board): { unit: number; nodes: Map<string, BoardNode>; offsets: Map<string, number> } {
  const nodes = new Map(board.nodes.map(n => [n.id, n]));
  const nearest = board.nodes.map(n => Math.min(...board.nodes.filter(m => m !== n).map(m => Math.hypot(m.x - n.x, m.y - n.y)))).filter(Number.isFinite).sort((a, b) => a - b);
  const median = nearest.length ? nearest[Math.floor(nearest.length / 2)]! : Math.max(board.width, board.height) / 10;
  const unit = Math.max(Math.min(Math.max(board.width, board.height) / 100, median / 7), Math.max(board.width, board.height) / 400);
  const groups = new Map<string, BoardEdge[]>();
  for (const e of board.edges) {
    const key = e.a < e.b ? `${e.a}|${e.b}` : `${e.b}|${e.a}`;
    groups.set(key, [...(groups.get(key) ?? []), e]);
  }
  const offsets = new Map<string, number>();
  for (const list of groups.values()) list.forEach((e, i) => {
    // Offsets are measured relative to the a→b direction, so flip reversed edges to keep them apart.
    const sign = e.a < e.b ? 1 : -1;
    offsets.set(e.id, (i - (list.length - 1) / 2) * unit * 1.8 * sign);
  });
  return { unit, nodes, offsets };
}

function edgeSegments(e: BoardEdge, a: BoardNode, b: BoardNode, unit: number, offset: number) {
  const count = e.segments ?? 1;
  const dx = b.x - a.x, dy = b.y - a.y;
  const length = Math.hypot(dx, dy);
  const inset = unit * 1.6;
  const usable = Math.max(length - inset * 2, 1);
  const gap = Math.min(unit * 0.35, usable / (count * 4));
  const segment = (usable - gap * (count - 1)) / count;
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  const items = [];
  for (let i = 0; i < count; i++) {
    const start = inset + i * (segment + gap);
    items.push(<rect key={i} x={start} y={offset - unit * 0.6} width={segment} height={unit * 1.2} rx={unit * 0.25} className="board-segment" />);
  }
  // A wider transparent stroke makes thin routes easy to hit.
  items.push(<line key="hit" x1={inset} y1={offset} x2={inset + usable} y2={offset} className="board-hit" strokeWidth={unit * 1.6} />);
  return <g transform={`translate(${a.x} ${a.y}) rotate(${angle})`}>{items}</g>;
}
